/**
 * CR500: Block Registry for DealCardSpec v2
 * CR318: Extension seam documentation for adding new details blocks
 * 
 * Maps block types to their React components.
 * All blocks receive standardized DealCardBlockProps.
 * 
 * EXTENSION SEAM (CR318):
 * To add a new details block (e.g., "requirements", "eligibility", "claim_steps"):
 * 
 * 1. Create the block component in src/lib/deal-cards/blocks/
 *    - Implement DealCardBlockProps<Deal> interface
 *    - Return null when block.enabled is false or no data
 *    - Use context.labels for configurable titles
 * 
 * 2. Add to this registry:
 *    - Import the component
 *    - Add entry: newBlock: NewBlockComponent
 * 
 * 3. Add to specV2/types.ts DealCardBlockType union
 * 
 * 4. Add to specV2/blockMetadata.ts:
 *    - Add to UNIVERSAL_DETAILS_BLOCK_IDS
 *    - Add entry to DETAILS_BLOCK_METADATA with uiGroup, defaultLabel, surface
 * 
 * 5. Update variant registry (database migration):
 *    - Add block to deal_card_template_variants.template_json.surfaces.details.blocks[]
 *    - Set enabled: false for safe default
 * 
 * The block will then appear in:
 *    - Structure tab (Back surface section)
 *    - V2 Debug Inspector
 *    - DetailsBlocksRenderer output
 */

import type { FC } from "react";
import type { DealCardBlockType, DealCardBlockProps } from "@/lib/deal-cards/specV2/types";
import type { Deal } from "@/lib/types/deals";

// Block components - imported as they're created
import { HeadlineBlock } from "./HeadlineBlock";
import { DatesBlock } from "./DatesBlock";
import { CTAButtonBlock } from "./CTAButtonBlock";
import { RibbonBlock } from "./RibbonBlock";
import { PromoBadgesBlock } from "./PromoBadgesBlock";
import { DetailsPillsBlock } from "./DetailsPillsBlock";
import { ActionsBlock } from "./ActionsBlock";
import { FeedbackBlock } from "./FeedbackBlock";
// Phase 2B: Details surface blocks
import { TermsBlock } from "./TermsBlock";
import { FinePrintBlock } from "./FinePrintBlock";
import { SourceBlock } from "./SourceBlock";
import { EvidenceBlock } from "./EvidenceBlock";
import { ValidatorBlock } from "./ValidatorBlock";
// Phase 2C: Value area blocks
import { ValueBoxBlock } from "./ValueBoxBlock";
import { SpendBoxBlock } from "./SpendBoxBlock";
import { RewardBoxBlock } from "./RewardBoxBlock";
// Phase 2E: Header block
import { HeaderBlock } from "./HeaderBlock";
// Phase 3: Starburst block
import { StarburstBlock } from "./StarburstBlock";
// Phase 4A: Meta pills block (deal type + status)
import { MetaPillsBlock } from "./MetaPillsBlock";
// Phase 5A: Back-face blocks
import { BackHeaderBlock } from "./BackHeaderBlock";
import { BackNavBlock } from "./BackNavBlock";
import { DetailsActionsBlock } from "./DetailsActionsBlock";
// CR321: Eligibility box block
import { EligibilityBoxBlock } from "./EligibilityBoxBlock";
// CR309: Details CTA block
import { DetailsCTABlock } from "./DetailsCTABlock";
// CR332: Flyer snippet block
import { FlyerSnippetBlock } from "./FlyerSnippetBlock";
// CR335: Flyer snippet details block (card back, admin-only)
import { FlyerSnippetDetailsBlock } from "./FlyerSnippetDetailsBlock";
// CR481: Card Snippet (configurable with eligibility + fallback)
import { CardSnippetBlock } from "./CardSnippetBlock";

// Placeholder component for blocks not yet extracted (debug mode only)
function PlaceholderBlock(props: DealCardBlockProps<Deal>) {
  // Only render in debug mode - check for ?dealCardV2debug=1
  if (typeof window !== "undefined") {
    const params = new URLSearchParams(window.location.search);
    if (params.get("dealCardV2debug") !== "1") {
      return null; // Silent skip in non-debug mode
    }
  }
  
  return (
    <div className="text-xs text-muted-foreground bg-muted/30 px-2 py-1 rounded border border-dashed border-muted-foreground/30">
      [Placeholder: {props.block.type}]
    </div>
  );
}

// Type for the registry
type BlockRegistry = Record<DealCardBlockType, FC<DealCardBlockProps<Deal>>>;

/**
 * Registry mapping block types to their components.
 * Components are added here as they're extracted from DealGiftCard.
 * 
 * Phase 1 extracted blocks: headline, dates, cta_button
 * Phase 2A extracted blocks: ribbon, promo_badges, details_pills, actions
 * Phase 2B extracted blocks: terms, fine_print, source, evidence, validator
 * Phase 5A extracted blocks: back_header, back_nav, details_actions
 */
export const dealCardBlockRegistry: BlockRegistry = {
  // Extracted blocks - Phase 1
  headline: HeadlineBlock,
  dates: DatesBlock,
  cta_button: CTAButtonBlock,
  
  // Extracted blocks - Phase 2A
  ribbon: RibbonBlock as FC<DealCardBlockProps<Deal>>,
  promo_badges: PromoBadgesBlock as FC<DealCardBlockProps<Deal>>,
  details_pills: DetailsPillsBlock as FC<DealCardBlockProps<Deal>>,
  actions: ActionsBlock as FC<DealCardBlockProps<Deal>>,
  feedback: FeedbackBlock as FC<DealCardBlockProps<Deal>>,
  
  // Extracted blocks - Phase 2B (Details surface)
  terms: TermsBlock,
  fine_print: FinePrintBlock,
  source: SourceBlock,
  evidence: EvidenceBlock,
  validator: ValidatorBlock,
  
  // Extracted blocks - Phase 2C (Value area)
  value_box: ValueBoxBlock,
  spend_box: SpendBoxBlock,
  reward_box: RewardBoxBlock,
  
  // Phase 2E: Header block (now extracted)
  header: HeaderBlock,
  
  // Phase 3: Starburst block (now extracted)
  starburst: StarburstBlock as FC<DealCardBlockProps<Deal>>,
  
  // Phase 4A: Meta pills block (deal type + status)
  meta_pills: MetaPillsBlock as FC<DealCardBlockProps<Deal>>,
  
  // Phase 5A: Back-face blocks
  back_header: BackHeaderBlock,
  back_nav: BackNavBlock,
  details_actions: DetailsActionsBlock,
  
  // CR316: Universal blocks - placeholders for now
  confidence_indicator: PlaceholderBlock,
  debug_strip: PlaceholderBlock,
  merchant_category_chips: PlaceholderBlock,
  
  // CR321: Eligibility box (details surface)
  eligibility_box: EligibilityBoxBlock,
  eligibility: EligibilityBoxBlock, // Alias for details surface block ID
  
  // CR309: Details CTA block
  details_cta: DetailsCTABlock as FC<DealCardBlockProps<Deal>>,
  // CR323: Legacy alias for backwards compatibility
  cta_details: DetailsCTABlock as FC<DealCardBlockProps<Deal>>,
  
  // CR332: Flyer snippet block (primary surface)
  flyer_snippet: FlyerSnippetBlock,
  // CR335: Flyer snippet details block (details surface, admin-only)
  flyer_snippet_details: FlyerSnippetDetailsBlock,
  // CR481: Card Snippet (configurable with eligibility + fallback)
  card_snippet: CardSnippetBlock,
};

/**
 * Get a block component from the registry.
 * Returns the placeholder if the block type is not registered.
 */
export function getBlockComponent(blockType: DealCardBlockType): FC<DealCardBlockProps<Deal>> {
  return dealCardBlockRegistry[blockType] || PlaceholderBlock;
}

/**
 * Check if a block type has been fully extracted (not a placeholder).
 */
export function isBlockExtracted(blockType: DealCardBlockType): boolean {
  const component = dealCardBlockRegistry[blockType];
  return component !== PlaceholderBlock;
}

/**
 * Get list of all extracted block types.
 */
export function getExtractedBlockTypes(): DealCardBlockType[] {
  const entries = Object.entries(dealCardBlockRegistry) as Array<[DealCardBlockType, unknown]>;
  return entries
    .filter(([, component]) => component !== PlaceholderBlock)
    .map(([type]) => type);
}

/**
 * Get list of blocks still using placeholders.
 */
export function getPlaceholderBlockTypes(): DealCardBlockType[] {
  const entries = Object.entries(dealCardBlockRegistry) as Array<[DealCardBlockType, unknown]>;
  return entries
    .filter(([, component]) => component === PlaceholderBlock)
    .map(([type]) => type);
}
